"use client";

import { Icon } from "@/components/ui/Icon";
import { motion } from "framer-motion";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from "@/components/ui/navigation-menu";
import Image from "next/image";
import logo from "@/assets/images/logo/logo-original-ombres-fond-blanc.png";

import logo2 from "@/assets/images/logo/forme logo blanc - PNG fond transparent.png";
import { COMPANY } from "@/config/company";
import { localeFromPath, stripLocale } from "@/config/i18n";
import { localeNavHref } from "@/config/localeRoutes";
import { HEADER_NAV } from "@/components/layout/navDict";
import { HeaderNavDropdown } from "@/components/layout/HeaderNavDropdown";
import { HeaderMobileNavSection } from "@/components/layout/HeaderMobileNavSection";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";
import { SearchTrigger } from "@/components/search/SearchTrigger";

const MotionLink = motion.create(Link);

const driverServices = HEADER_NAV.driverServices;
const rentalServices = HEADER_NAV.rentalServices;
const partnerServices = HEADER_NAV.partnerServices;

export function Header() {
  const pathname = usePathname();
  const currentPath = pathname ?? "";
  const locale = localeFromPath(currentPath);
  const t = HEADER_NAV;
  // Détection de contexte indépendante de la locale : on raisonne sur le chemin
  // FR canonique (sans préfixe /en) pour que les pages EN gardent le même
  // habillage (logo, accents or) que leurs jumelles FR.
  const routePath = stripLocale(currentPath);
  const isLocationRoute = routePath.startsWith("/location");
  const isLandingRoute = routePath === "/";
  const neutralRoutes = [
    "/contact",
    "/mentions-legales",
    "/politique-de-confidentialite",
    "/cgv",
    "/cgu",
    "/plan-du-site",
  ];
  const isNeutralRoute = neutralRoutes.some(
    (route) => routePath === route || routePath.startsWith(`${route}/`),
  );
  const isLandingLikeRoute = isLandingRoute || isNeutralRoute;
  const selectedLogo = isLocationRoute ? logo2 : logo;
  const chauffeurCta = {
    href: localeNavHref("/reservation", locale),
    label: t.cta.book[locale],
    icon: "heroicons:calendar",
  };
  const locationCta = {
    href: localeNavHref("/location/vehicules#catalogue-location", locale),
    label: t.cta.rent[locale],
    icon: "ph:car",
  };
  // CTA principal = réservation chauffeur PARTOUT (priorité n°1 de l'entreprise),
  // sauf sur les pages location où « Louer » devient le CTA contextuel.
  const primaryCta = isLocationRoute ? locationCta : chauffeurCta;
  const secondaryCta = primaryCta === locationCta ? chauffeurCta : locationCta;
  const primaryCtaShortLabel =
    primaryCta === chauffeurCta
      ? t.cta.bookShort[locale]
      : t.cta.rentShort[locale];
  const tickingRef = useRef(false);
  const burgerRef = useRef<HTMLButtonElement | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDriver, setOpenDriver] = useState(false);
  const [openRental, setOpenRental] = useState(false);
  const [openPartners, setOpenPartners] = useState(false);
  const [headerBgOpacity, setHeaderBgOpacity] = useState(0.5);

  useEffect(() => {
    // Largeur du header désormais STABLE (95vw via CSS) : on ne rétrécit plus le
    // pill au scroll. L'ancien rétrécissement (95vw -> plancher 1400px) passait
    // sous la largeur du contenu dense (2 CTA dès 2xl) et forçait les libellés à
    // wrapper sur 2 lignes (effet « cambré »). Seule l'opacité du fond varie.
    const computeBg = () => {
      const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
      const viewportHeight = Math.max(
        window.innerHeight,
        document.documentElement.clientHeight || 0,
        1,
      );
      // Opacité du fond : 0.5 (en haut) -> 1 (après 100vh de scroll).
      const progress = Math.min(Math.max(scrollY / viewportHeight, 0), 1);
      setHeaderBgOpacity(0.5 + 0.5 * progress);
    };

    const onScrollOrResize = () => {
      if (tickingRef.current) return;
      tickingRef.current = true;
      requestAnimationFrame(() => {
        computeBg();
        tickingRef.current = false;
      });
    };

    computeBg();
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, []);

  useEffect(() => {
    // Ferme le menu mobile lors d'un changement de route
    const frame = requestAnimationFrame(() => {
      setMobileOpen(false);
      setOpenDriver(false);
      setOpenRental(false);
      setOpenPartners(false);
    });
    return () => cancelAnimationFrame(frame);
  }, [currentPath]);

  // Fermeture du menu mobile à la touche Échap, avec retour du focus sur le
  // bouton burger (gestion clavier de la disclosure mobile).
  useEffect(() => {
    if (!mobileOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMobileOpen(false);
        burgerRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [mobileOpen]);

  return (
    <motion.header
      className={
        "pointer-events-none fixed left-0 right-0 top-4 z-50 bg-transparent transition-all duration-300 ease-in-out"
      }
      initial={false}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <motion.div
        className={`pointer-events-auto relative mx-auto flex w-[95vw] min-w-0 max-w-[95vw] items-center justify-between gap-x-3 rounded-full border px-4 py-2 sm:px-6 lg:gap-x-4 xl:gap-x-6 ${
          routePath === "/location/vehicules"
            ? "border-neutral-700"
            : "border-neutral-500"
        }`}
        animate={{
          backgroundColor: routePath.startsWith("/location/vehicules")
            ? "rgb(0,0,0)"
            : `rgba(0,0,0, ${headerBgOpacity})`,
        }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="flex items-center gap-x-4 lg:gap-x-6">
          {/* Burger (visible < lg) */}
          <button
            ref={burgerRef}
            type="button"
            aria-label={mobileOpen ? t.closeMenu[locale] : t.openMenu[locale]}
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav-menu"
            className={`inline-flex h-10 w-10 items-center justify-center rounded-md text-white transition-colors lg:hidden ${
              isLocationRoute ? "hover:text-gold-200" : "hover:text-primary"
            }`}
            onClick={() => setMobileOpen((v) => !v)}
          >
            <Icon
              icon={mobileOpen ? "mdi:close" : "mdi:menu"}
              className="h-6 w-6"
            />
          </button>
          <MotionLink
            href={locale === "fr" ? "/" : `/${locale}`}
            className="flex shrink-0 items-center space-x-2"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {/* `sizes` est indispensable ici : le logo est importé à sa taille
                source (1656 px de large) mais rendu en `h-8` (32 px). Sans
                `sizes`, next/image génère un srcSet 1x/2x calé sur la largeur
                intrinsèque et le navigateur téléchargeait la variante 3840 px
                (~70 Ko d'AVIF) — préchargée en priorité haute via `priority`,
                donc en concurrence directe avec l'image LCP. Avec `sizes`, le
                srcSet passe en descripteurs `w` et le navigateur choisit une
                variante de 48/96 px (~2 Ko). */}
            <Image
              src={selectedLogo}
              alt="KS Rent"
              width={(selectedLogo as any).width}
              height={(selectedLogo as any).height}
              sizes="41px"
              className="h-8 w-auto"
              priority
            />
          </MotionLink>

          {/* Navigation desktop uniquement (>= lg) */}
          <NavigationMenu
            dir={locale === "ar" ? "rtl" : "ltr"}
            className="hidden lg:block"
          >
            <NavigationMenuList>
              {/* Pilier Chauffeur — trigger = disclosure pur (button), sans
                  <Link> imbriqué (interactif-dans-interactif invalide). L'accès
                  aux pages se fait via les éléments du menu. */}
              <HeaderNavDropdown
                label={t.triggerChauffeur[locale]}
                items={driverServices}
                locale={locale}
                isLocationRoute={isLocationRoute}
              />

              {/* Pilier Location de véhicules. */}
              <HeaderNavDropdown
                label={t.triggerLocation[locale]}
                items={rentalServices}
                locale={locale}
                isLocationRoute={isLocationRoute}
              />

              {/* Pilier B2B/pro (apporteurs, conciergeries, hôtels…). */}
              <HeaderNavDropdown
                label={t.triggerPartners[locale]}
                items={partnerServices}
                locale={locale}
                isLocationRoute={isLocationRoute}
              />

              <NavigationMenuItem className="hidden xl:flex">
                <NavigationMenuLink asChild>
                  <Link
                    href={localeNavHref("/a-propos", locale)}
                    className={`whitespace-nowrap text-sm font-semibold text-white transition-colors ${
                      isLocationRoute
                        ? "hover:text-gold-200"
                        : "hover:text-primary"
                    }`}
                  >
                    {t.about[locale]}
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
        </div>

        <div className="flex items-center gap-x-4">
          {/* Numero cliquable dans la barre desktop.
              Pourquoi : le desktop pese 26 413 impressions et 128 clics sur 12
              mois (Search Console, releve du 20/09/2026) et n'avait AUCUN point
              d'appel permanent — la barre d'action fixe est `md:hidden`, donc
              reservee au mobile. Un visiteur desktop devait descendre jusqu'au
              pied de page pour trouver un numero.
              Le header est dense : un debordement est connu entre 1536 et
              1700px. Le numero n'apparait donc EN TEXTE qu'a partir de 1750px,
              et reste une pastille icone seule entre 1024 et 1750px. Mesure
              faite a 1440px et 1600px. */}
          <a
            href={`tel:${COMPANY.phoneIntl}`}
            aria-label={`Appeler KS Rent Services au ${COMPANY.phoneDisplay}`}
            title={COMPANY.phoneDisplay}
            className={`hidden items-center gap-2 whitespace-nowrap rounded-lg border px-3 py-2 text-sm font-semibold transition-colors duration-300 lg:inline-flex ${
              isLocationRoute
                ? "border-white/30 text-white hover:border-gold-200 hover:text-gold-200"
                : "border-white/25 text-gray-100 hover:border-primary hover:text-primary"
            }`}
          >
            <Icon icon="ph:phone-fill" className="h-4 min-h-4 w-4 min-w-4" />
            <span className="hidden min-[1750px]:inline">
              {COMPANY.phoneDisplay}
            </span>
          </a>

          {/* Un SEUL CTA dans la barre desktop (chauffeur privé = priorité #1).
              La location reste accessible via le dropdown « Location de Véhicules »
              et redevient le CTA principal sur les pages /location (logique
              contextuelle primaryCta/secondaryCta). Le 2e CTA reste présent dans
              le menu mobile (espace vertical suffisant). Densité réduite → plus de
              débordement (logo éjecté / libellés wrappés) sur la bande 1536-1700px. */}
          <MotionLink
            href={localeNavHref("/contact", locale)}
            className={`hidden text-sm font-semibold text-gray-200 transition-colors duration-300 xl:inline-block ${
              isLocationRoute ? "hover:text-gold-200" : "hover:text-primary"
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {t.contact[locale]}
          </MotionLink>

          <MotionLink
            href={primaryCta.href}
            className={`group flex items-center space-x-2 whitespace-nowrap rounded-lg px-6 py-2.5 text-sm font-semibold shadow-md transition-all duration-300 focus:outline-none focus:ring-2 ${
              isLandingLikeRoute
                ? "border border-white/35 bg-white/10 text-white hover:border-gold-200 hover:text-gold-200 focus:ring-gold-200 focus:ring-offset-2"
                : isLocationRoute
                  ? "bg-white text-primaryLoc hover:bg-primaryLoc hover:text-gold-200 focus:ring-gold-200 focus:ring-offset-2"
                  : "bg-primary text-black hover:bg-gold-600 hover:text-white focus:ring-primary focus:ring-offset-2"
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Icon
              icon={primaryCta.icon}
              className={`h-5 min-h-5 w-5 min-w-5 ${
                isLocationRoute
                  ? "text-primaryLoc group-hover:text-gold-200"
                  : isLandingLikeRoute
                    ? "text-white group-hover:text-gold-200"
                    : ""
              }`}
            />
            <span className="hidden xl:inline">{primaryCta.label}</span>
            <span className="inline xl:hidden">{primaryCtaShortLabel}</span>
          </MotionLink>

          {/* Recherche globale (desktop) */}
          <SearchTrigger variant="desktop" className="hidden lg:inline-flex" />

          {/* Sélecteur de langue (desktop) */}
          <LanguageSwitcher
            variant="header"
            className="hidden lg:inline-flex"
          />
        </div>

        {/* Menu mobile (dropdown) */}
        {mobileOpen && (
          <div
            id="mobile-nav-menu"
            className="absolute left-0 right-0 top-[calc(100%+12px)] z-50 max-h-[calc(100vh-120px)] overflow-y-auto overscroll-contain rounded-2xl border border-neutral-700 bg-black/95 p-3 shadow-2xl backdrop-blur-md lg:hidden"
          >
            <div>
              {/* Recherche — accès rapide, en haut */}
              <SearchTrigger variant="mobile" />

              {/* CTA principaux : réservation chauffeur en avant (rempli),
                  location en secondaire (contour). Plus de badges. */}
              <div className="mt-3 grid gap-2">
                <Link
                  href={primaryCta.href}
                  className={`flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold shadow-sm transition-colors ${
                    isLocationRoute
                      ? "bg-white text-primaryLoc hover:bg-gold-200"
                      : "bg-primary text-black hover:bg-gold-600 hover:text-white"
                  }`}
                >
                  <Icon icon={primaryCta.icon} className="h-5 w-5" />
                  {primaryCta.label}
                </Link>
                <Link
                  href={secondaryCta.href}
                  className="flex items-center justify-center gap-2 rounded-xl border border-white/20 px-4 py-3 text-sm font-semibold text-white transition-colors hover:border-primary hover:text-primary"
                >
                  <Icon icon={secondaryCta.icon} className="h-5 w-5" />
                  {secondaryCta.label}
                </Link>
              </div>

              {/* Navigation */}
              <nav className="mt-3 divide-y divide-white/10 border-t border-white/10">
                {/* Chauffeur Privé */}
                <HeaderMobileNavSection
                  label={t.triggerChauffeur[locale]}
                  items={driverServices}
                  open={openDriver}
                  onToggle={() => setOpenDriver((v) => !v)}
                  locale={locale}
                  isLocationRoute={isLocationRoute}
                />

                {/* Location de Véhicules */}
                <HeaderMobileNavSection
                  label={t.triggerLocation[locale]}
                  items={rentalServices}
                  open={openRental}
                  onToggle={() => setOpenRental((v) => !v)}
                  locale={locale}
                  isLocationRoute={isLocationRoute}
                />

                {/* Partenaires */}
                <HeaderMobileNavSection
                  label={t.triggerPartners[locale]}
                  items={partnerServices}
                  open={openPartners}
                  onToggle={() => setOpenPartners((v) => !v)}
                  locale={locale}
                  isLocationRoute={isLocationRoute}
                />

                {/* À propos */}
                <div className="py-2">
                  <Link
                    href={localeNavHref("/a-propos", locale)}
                    className={`block px-3 py-2 text-white transition-colors ${
                      isLocationRoute
                        ? "hover:text-gold-200"
                        : "hover:text-primary"
                    }`}
                  >
                    {t.about[locale]}
                  </Link>
                </div>

                {/* Contact */}
                <div className="py-2">
                  <Link
                    href={localeNavHref("/contact", locale)}
                    className={`block px-3 py-2 text-white transition-colors ${
                      isLocationRoute
                        ? "hover:text-gold-200"
                        : "hover:text-primary"
                    }`}
                  >
                    {t.contact[locale]}
                  </Link>
                </div>
              </nav>

              {/* Sélecteur de langue (en bas) */}
              <div className="mt-3 flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5">
                <span className="text-sm font-semibold text-white/70">
                  {locale === "en" ? "Language" : "Langue"}
                </span>
                <LanguageSwitcher variant="header" />
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </motion.header>
  );
}
